import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { Album, MemoirSummary } from '../../../../types'
import { apiClient } from '../lib/api-client'
import { ApiError } from '../lib/errors'
import { SUPPORT_EMAIL } from '../lib/legal'
import { STORAGE_KEY } from '../hooks/use-album-collection'
import ConfirmModal from './ui/confirm-modal'

/** After a handover this device must stop showing the Memoir's cached albums. */
function forgetMemoirAlbums(memoirId: string): void {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) return
    const kept = (JSON.parse(stored) as Album[]).filter((a) => a.memoirId !== memoirId)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(kept))
  } catch { /* the next fetch prunes it */ }
}

/** What to tell the photographer when an invitation step is refused. */
export function inviteErrorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    switch (e.code) {
      case 'INVALID_EMAIL': return 'That email address does not look right. Please check it and try again.'
      case 'MEMOIR_INVITE_PENDING': return 'This Memoir already has an open invitation. Withdraw it first to invite someone else.'
      case 'MEMOIR_BUSY': return 'This Memoir cannot be shared right now. Pay for it again first, or try later.'
      case 'RATE_LIMITED': return 'Too many invitations just now. Please try again in a little while.'
      case 'CLAIMANT_NOT_FOUND': return 'We could not find that person. Please use their email address.'
      case 'MEMOIR_INVITE_NOT_ACCEPTED': return 'Your client has not accepted yet.'
      case 'INVITE_MAIL_FAILED':
        return 'The invitation is saved, but we could not email it. Withdraw it and send it again, '
          + `or write to ${SUPPORT_EMAIL}.`
    }
  }
  return `Something went wrong. Please try again, or write to ${SUPPORT_EMAIL}.`
}

/**
 * Share a Memoir with the client who will keep it (SOL-185), on the owner's
 * Memoir page: invite by email, then see the invitation's state, withdraw it,
 * and once accepted hand the Memoir over.
 *
 * The server also accepts the user id of a client who opened an album here,
 * but the albums payload names no one, so there is nothing to pick from yet:
 * the email field is the whole form.
 */
export default function MemoirHandover({
  memoir,
  onChanged,
}: {
  memoir: MemoirSummary
  onChanged?: () => void
}): JSX.Element | null {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirming, setConfirming] = useState(false)

  if (memoir.handedOverAt) return null
  // Nothing to share while the Memoir is waiting to be paid for again.
  if (memoir.status === 'grace' && !memoir.lapsed) return null

  async function run(fn: () => Promise<unknown>, after?: () => void): Promise<void> {
    setBusy(true)
    setError(null)
    try {
      await fn()
      after?.()
      onChanged?.()
    } catch (e) {
      setError(inviteErrorMessage(e))
      // An invite whose mail failed still stands: show it so it can be withdrawn.
      if (e instanceof ApiError && e.code === 'INVITE_MAIL_FAILED') onChanged?.()
    } finally {
      setBusy(false)
      setConfirming(false)
    }
  }

  const invite = memoir.invite

  return (
    <div className="memoir-handover">
      {!invite && (
        <form
          className="memoir-handover__form"
          onSubmit={(e) => {
            e.preventDefault()
            const value = email.trim()
            if (!value) return
            void run(() => apiClient.inviteToMemoir(memoir.uuid, { email: value }), () => setEmail(''))
          }}
        >
          <h3 className="memoir-handover__title">Share this Memoir with your client</h3>
          <p className="memoir-handover__hint">
            They will get an email, and once they accept you can hand the Memoir over to them to keep.
          </p>
          <label className="memoir-handover__label">
            Client's email
            <input
              type="email"
              className="memoir-handover__input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="off"
            />
          </label>
          <button type="submit" className="memoir-payment__action" disabled={busy || !email.trim()}>
            Send invitation
          </button>
        </form>
      )}

      {invite && !invite.acceptedAt && (
        <p className="memoir-handover__state" role="status">
          Invited {invite.email}.{' '}
          <button type="button" className="memoir-payment__refund" disabled={busy}
            onClick={() => void run(() => apiClient.withdrawMemoirInvite(memoir.uuid))}>
            Withdraw
          </button>
        </p>
      )}

      {invite?.acceptedAt && (
        <p className="memoir-handover__state" role="status">
          {invite.email} accepted.{' '}
          <button type="button" className="memoir-payment__action" disabled={busy}
            onClick={() => setConfirming(true)}>
            Hand it over now
          </button>{' '}
          <button type="button" className="memoir-payment__refund" disabled={busy}
            onClick={() => void run(() => apiClient.withdrawMemoirInvite(memoir.uuid))}>
            Withdraw
          </button>
        </p>
      )}

      {error && <p className="memoir-payment__error" role="alert">{error}</p>}

      <ConfirmModal
        isOpen={confirming}
        title="Hand this Memoir over?"
        message={`It goes to ${invite?.email ?? 'your client'} to keep. You won't be able to see or change this Memoir afterwards.`}
        confirmText="Hand it over"
        variant="warning"
        isLoading={busy}
        onConfirm={() => void run(() => apiClient.handOverMemoir(memoir.uuid), () => { forgetMemoirAlbums(memoir.uuid); navigate('/') })}
        onCancel={() => setConfirming(false)}
      />
    </div>
  )
}
